// Picking a team from a big collection (tournament team, the Trail line-up): the team as cards (tap one, then another
// to swap, or a creature below to replace it; ✕ takes it out) and your creatures below with search, an element filter,
// the best of each species by default, strongest first, a page at a time. Each screen keeps its own PickState and
// passes its own act prefix; clicks come back through pickClick, inputs through pickInput.
import { SPECIES } from '../shared/species.ts';
import { ELEMENTS, ELEMENT_IDS } from '../shared/elements.ts';
import { arenaPower } from '../shared/power.ts';
import type { ArenaCreature } from '../shared/arena.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { spriteUrl } from './assets.ts';
import { esc, full, cname } from './fmt.ts';
import { ICON } from './icons.ts';

export interface PickState { pick: number | null; q: string; el: string; all: boolean; n: number }
// Cards drawn at once; the dialog pages them (fitlist), Show more past this.
const PAGE = 200;
export const pickState = (): PickState => ({ pick: null, q: '', el: '', all: false, n: PAGE });

const spName = (id: string) => SPECIES[id]?.name ?? id;

/** A creature as a card: sprite, name, level and Power (as it would fight). */
export function card(c: ArenaCreature, tags = '', cls = ''): string {
  return `<span class="tn-cd${c.rolls.aura ? ' aura' : ''}${cls ? ` ${cls}` : ''}"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt=""><b>${esc(spName(c.species))}</b><small>Lv ${full(c.level)}<span class="tn-pw"> · ${full(arenaPower(c))}</span></small>${tags}</span>`;
}

export interface PoolItem { c: CreatureDTO; a: ArenaCreature; p: number }
const pools = new Map<string, { key: string; list: PoolItem[] }>();
/**
 * Every creature that may join, as it would fight, strongest first. Kept per `name` until `key` changes (the collection
 * size and anything the rules depend on), so typing in the search doesn't redo the maths over a big collection.
 */
export function pool(name: string, key: string, ok: (c: CreatureDTO) => boolean, asFought: (c: CreatureDTO) => ArenaCreature): PoolItem[] {
  const k = `${key}|${S.creatures.size}`;
  const hit = pools.get(name);
  if (hit?.key === k) return hit.list;
  const list: PoolItem[] = [];
  for (const c of S.creatures.values()) {
    if (!ok(c)) continue;
    const a = asFought(c);
    list.push({ c, a, p: arenaPower(a) });
  }
  list.sort((x, y) => y.p - x.p);
  pools.set(name, { key: k, list });
  return list;
}

export interface PickerOpts {
  /** data-act prefix: `${act}-slot`, `${act}-x`, `${act}-bench`, `${act}-best`, `${act}-more`; inputs carry data-pk="act". */
  act: string;
  st: PickState;
  /** The team in order, as it would fight. */
  team: ArenaCreature[];
  max: number;
  items: PoolItem[];
  /** One of each species in a team. */
  unique?: boolean;
}

/** The team strip, the search tools, and your creatures to choose from (grid: the cards and Show more). */
export function pickerHtml(o: PickerOpts): { strip: string; tools: string; grid: string } {
  const { st, act } = o;
  const strip = `<div class="tn-strip" style="--slots:${o.max}">${Array.from({ length: o.max }, (_, i) => {
    const c = o.team[i];
    if (!c) return `<div class="tn-slot empty"><span class="tn-cd"><i>${i + 1}</i></span></div>`;
    const on = st.pick === i;
    return `<div class="tn-slot${on ? ' on' : ''}"><button class="tn-sb" data-act="${act}-slot" data-id="${i}" aria-pressed="${on}">${card(c)}</button>${on ? `<button class="tn-x" data-act="${act}-x" data-id="${i}" aria-label="Take out">✕</button>` : ''}</div>`;
  }).join('')}</div>`;
  const ids = new Set(o.team.map((c) => c.id));
  const taken = new Set(o.team.map((c) => c.species));
  const swapping = st.pick !== null ? o.team[st.pick]?.species : undefined;
  const q = st.q.trim().toLowerCase();
  const seen = new Set<string>();
  const shown: PoolItem[] = [];
  let total = 0;
  for (const x of o.items) {
    const sp = x.c.species;
    if (ids.has(x.c.id) || (o.unique && taken.has(sp) && sp !== swapping)) continue;
    const els = (SPECIES[sp]?.elements ?? []) as string[];
    if (st.el && !els.includes(st.el)) continue;
    if (q && !(spName(sp).toLowerCase().includes(q) || cname(x.c).toLowerCase().includes(q) || els.some((el) => el.includes(q) || ELEMENTS[el as keyof typeof ELEMENTS]?.name.toLowerCase().includes(q)))) continue;
    if (!st.all) { if (seen.has(sp)) continue; seen.add(sp); }
    total++;
    if (shown.length < st.n) shown.push(x);
  }
  const tools = `<div class="tn-btools"><label class="fsearch">${ICON.search}<input data-pk="${act}" data-pk-q placeholder="Name, species or element" value="${esc(st.q)}" aria-label="Search your creatures"></label>
    <select data-pk="${act}" data-pk-el aria-label="Element"><option value="">All elements</option>${ELEMENT_IDS.map((el) => `<option value="${el}"${st.el === el ? ' selected' : ''}>${ELEMENTS[el].name}</option>`).join('')}</select>
    <button class="btn small tog${st.all ? '' : ' on'}" data-act="${act}-best" aria-pressed="${!st.all}">Best of each species</button></div>`;
  const cards = shown.map((x) => `<button class="tn-bb" data-key="tb-${x.c.id}" data-act="${act}-bench" data-id="${x.c.id}">${card(x.a)}</button>`).join('');
  const grid = `<div class="tn-bench" data-fit="${act}-bench">${cards || '<p class="hint">None.</p>'}</div>${total > shown.length ? `<div class="tn-more"><span class="muted">${full(shown.length)} of ${full(total)}</span><button class="bv-btn" data-act="${act}-more">Show more</button></div>` : ''}`;
  return { strip, tools, grid };
}

/**
 * A picker click on `ids` (changed in place). Returns 'team' when the team changed, 'view' when only the picker did,
 * null when the act isn't the picker's.
 */
export function pickClick(act: string, prefix: string, id: string, st: PickState, ids: number[], max: number, min1 = false): 'team' | 'view' | null {
  if (!act.startsWith(`${prefix}-`)) return null;
  switch (act.slice(prefix.length + 1)) {
    case 'slot': {
      const i = Number(id);
      if (st.pick === null || st.pick === i) { st.pick = st.pick === i ? null : i; return 'view'; }
      [ids[i], ids[st.pick]] = [ids[st.pick], ids[i]];
      st.pick = null; return 'team';
    }
    case 'x': if (ids.length > 1 || !min1) { ids.splice(Number(id), 1); } st.pick = null; return 'team';
    case 'bench': {
      const cid = Number(id);
      if (!cid || ids.includes(cid)) return 'view';
      if (st.pick !== null && st.pick < ids.length) ids[st.pick] = cid;
      else if (ids.length < max) ids.push(cid);
      st.pick = null; return 'team';
    }
    case 'best': st.all = !st.all; st.n = PAGE; return 'view';
    case 'more': st.n += PAGE * 2; return 'view';
  }
  return null;
}

/** The search box and element filter: true when `el` belongs to the picker `prefix`. */
export function pickInput(el: HTMLInputElement | HTMLSelectElement, prefix: string, st: PickState): boolean {
  if (el.dataset.pk !== prefix) return false;
  if (el.dataset.pkQ !== undefined) st.q = el.value;
  else if (el.dataset.pkEl !== undefined) st.el = el.value;
  else return false;
  st.n = PAGE;
  return true;
}

/** A team as cards, read only (empty places dashed); tapping any opens the builder through `act`. */
export function teamStrip(team: ArenaCreature[], max: number, act: string, tags?: (c: ArenaCreature) => string): string {
  return `<div class="tn-strip" style="--slots:${max}">${Array.from({ length: max }, (_, i) => {
    const c = team[i];
    return c ? `<div class="tn-slot"><button class="tn-sb" data-act="${act}">${card(c, tags?.(c) ?? '')}</button></div>` : `<div class="tn-slot empty"><button class="tn-sb" data-act="${act}"><span class="tn-cd"><i>+</i></span></button></div>`;
  }).join('')}</div>`;
}
